/**
 * Renders a single canvas node with all interactive controls.
 */
import React, { useState } from 'react';
import AddButton from 'components/ui/AddButton';
import SliceButton from 'components/ui/SliceButton';
import HamburgerButton from 'components/ui/HamburgerButton';
import StatusCircle from 'components/ui/StatusCircle';
import { NODE_W, MOSCOW_DOT, renderStatusPill, renderWrappedTitle, splitTitle, getNodeHeight, COLORS } from 'utils/canvasUtils';
import { getOutcomeProgressPercent, getOutcomeProgressColor } from 'utils/ostUtils';
import { NodeType, OstNode } from 'types';

const OUTCOME_PROGRESS_BAR_HEIGHT = 3;
const OUTCOME_PROGRESS_BAR_Y = -8;

const NODE_WITH_CHILD_TYPES = new Set(['outcome', 'opportunity', 'solution', 'story']);

type CanvasNodeProps = {
    node: OstNode;
    selectedNode: OstNode | null;
    nodeHeights: Record<string, number>;
    solutionChildType: NodeType;
    onMouseDown: (e: React.MouseEvent<SVGGElement>, nodeId: string) => void;
    onSelectNode: (node: OstNode | null) => void;
    onAddNode: (type: NodeType, parentId: string | null, data?: import('types').NewNodePayload) => void;
    onSlice: (type: 'opportunity' | 'story' | 'experiment', node: OstNode) => void;
    onManualSlice: (node: OstNode) => void;
    modifierKeyActive: boolean;
    connectMode: boolean;
    isConnectionSource?: boolean;
    isConnectionTarget?: boolean;
    onConnectHover?: (nodeId: string) => void;
    onConnectLeave?: () => void;
    suppressClickNodeId?: string | null;
    onConsumeSuppressedClick?: () => void;
    isInsightConnectionTarget?: boolean;
    /** True while the user drags a triage card and hovers it over this node. */
    isTriageDragTarget?: boolean;
    /** True while the user plain-drags another tree node and hovers it over this node
     * (see Canvas.tsx's connectDropTargetNodeId) — dropping here creates a connector. */
    isNodeConnectDropTarget?: boolean;
};

export default function CanvasNode({
    node,
    selectedNode,
    nodeHeights,
    solutionChildType,
    modifierKeyActive,
    connectMode,
    onMouseDown,
    onSelectNode,
    onAddNode,
    onSlice,
    onManualSlice,
    isConnectionSource,
    isConnectionTarget,
    isInsightConnectionTarget,
    isTriageDragTarget,
    isNodeConnectDropTarget,
    onConnectHover,
    onConnectLeave,
    suppressClickNodeId,
    onConsumeSuppressedClick,
}: CanvasNodeProps) {
    const c = COLORS[node.type] || { bg: '#fff', border: '#000', label: '#000' };
    const isStoryOrExperiment = node.type === 'story' || node.type === 'experiment' || node.type === 'enablerNode';
    const nodeHeight = nodeHeights[node.id] || getNodeHeight(node);
    const nodeX = node.x ?? 0;
    const nodeY = node.y ?? 0;
    const titleLines = splitTitle(node.title);
    const textStartY = 48;
    const [isHovered, setIsHovered] = useState(false);

    const cursorUrl = `${process.env.PUBLIC_URL || ''}/hand-cursor.cur`;
    const pinchCursorUrl = `${process.env.PUBLIC_URL || ''}/hand-pinch-cursor.cur`;
    const isPinchCursor = connectMode || (isHovered && modifierKeyActive);
    const cursorStyle = {
        cursor: isPinchCursor ? `url(${pinchCursorUrl}) 16 16, grab` : `url(${cursorUrl}) 16 16, grab`,
    };
    const nodeClassName = `node-grab ${isPinchCursor ? 'node-grab--pinch' : ''}`.trim();

    const renderActionControls = () => {
        if (selectedNode?.id !== node.id || !NODE_WITH_CHILD_TYPES.has(node.type)) return null;

        const circleY = nodeHeight;

        const showAdd = node.type === 'opportunity';
        // Solution nodes are treated as Epics and can be sliced into stories/experiments.
        const showSlice = node.type === 'opportunity' || node.type === 'story' || node.type === 'solution';
        const showNodePlus = node.type === 'outcome';
        const showManualSlice = node.type === 'solution';

        // When two buttons are visible, offset them so they don't overlap.
        // Solution nodes show slice on the left and hamburger on the right of center;
        // opportunity nodes show + on the left and slice on the right.
        const sliceX = node.type === 'solution'
            ? NODE_W / 2 - 20
            : (node.type === 'opportunity' && showAdd)
                ? NODE_W / 2 + 20
                : NODE_W / 2;

        return (
            <>
                {node.type === 'opportunity' && showAdd && (
                    <AddButton x={NODE_W / 2 - 20} y={circleY} onClick={() => onAddNode('solution', node.id)} />
                )}

                {showSlice && (
                    // For solution (Epic) nodes, slice into the global solutionChildType (story or experiment).
                    <SliceButton
                        x={sliceX}
                        y={circleY}
                        onClick={() =>
                            onSlice(
                                node.type === 'opportunity'
                                    ? 'opportunity'
                                    : node.type === 'solution'
                                        ? (solutionChildType as 'story' | 'experiment')
                                        : 'story',
                                node,
                            )
                        }
                    />
                )}

                {showManualSlice && (
                    <HamburgerButton x={NODE_W / 2 + 20} y={circleY} onClick={() => onManualSlice(node)} />
                )}

                {showNodePlus && (
                    <g
                        transform={`translate(${NODE_W / 2}, 0)`}
                        className='clickable'
                        onMouseDown={(e) => e.stopPropagation()}
                        onClick={(e) => {
                            e.stopPropagation();
                            const childType = node.type === 'outcome' ? 'opportunity' : solutionChildType || 'story';
                            onAddNode(childType, node.id);
                        }}
                    >
                        <circle cx={0} cy={circleY} r={10} fill='#444' />
                        <text
                            x={0}
                            y={circleY - 1.35}
                            fill='#fff'
                            fontSize={20}
                            fontWeight={300}
                            textAnchor='middle'
                            dominantBaseline='central'
                            className='no-select'
                            style={{ fontFamily: 'var(--font-monospace)' }}
                        >
                            +
                        </text>
                    </g>
                )}
            </>
        );
    };

    const stroke = c.border; // kept for non-triage use
    const strokeWidth = selectedNode?.id === node.id || isConnectionTarget || isInsightConnectionTarget || isTriageDragTarget || isNodeConnectDropTarget ? 3.5 : 1;
    const strokeColor = isTriageDragTarget || isNodeConnectDropTarget ? '#22c55e' : stroke;

    return (
        <g
            className={nodeClassName}
            style={cursorStyle}
            transform={`translate(${nodeX - NODE_W / 2}, ${nodeY - nodeHeight / 2})`}
            onMouseEnter={() => {
                setIsHovered(true);
                onConnectHover?.(node.id);
            }}
            onMouseLeave={() => {
                setIsHovered(false);
                onConnectLeave?.();
            }}
            onMouseDown={(e) => onMouseDown(e, node.id)}
            onClick={(e) => {
                e.stopPropagation();

                if (suppressClickNodeId) {
                    onConsumeSuppressedClick?.();
                    if (suppressClickNodeId === node.id) {
                        return;
                    }
                }

                onSelectNode(node);
            }}
        >
            {node.type === 'outcome' && (() => {
                const percent = getOutcomeProgressPercent(node);
                if (percent === null) return null;
                const filledWidth = (NODE_W * percent) / 100;

                return (
                    <g pointerEvents='none'>
                        <rect
                            x={0}
                            y={OUTCOME_PROGRESS_BAR_Y}
                            width={NODE_W}
                            height={OUTCOME_PROGRESS_BAR_HEIGHT}
                            rx={OUTCOME_PROGRESS_BAR_HEIGHT / 2}
                            fill='#e5e7eb'
                        />
                        <rect
                            x={0}
                            y={OUTCOME_PROGRESS_BAR_Y}
                            width={filledWidth}
                            height={OUTCOME_PROGRESS_BAR_HEIGHT}
                            rx={OUTCOME_PROGRESS_BAR_HEIGHT / 2}
                            fill={getOutcomeProgressColor(percent)}
                        />
                    </g>
                );
            })()}

            <rect
                width={NODE_W}
                height={nodeHeight}
                rx={2}
                fill={c.bg}
                stroke={strokeColor}
                strokeWidth={strokeWidth}
                filter='url(#node-shadow)'
            />

            {renderStatusPill(
                c.border,
                isStoryOrExperiment && node.storySize
                    ? `${c.label} [${node.storySize}]`
                    : c.label,
            )}
            {renderWrappedTitle(titleLines, textStartY)}

            {(isStoryOrExperiment || node.type === 'solution') && (
                <circle
                    cx={NODE_W - 12}
                    cy={nodeHeight - 12}
                    r={4}
                    fill={MOSCOW_DOT[(node.moscow as keyof typeof MOSCOW_DOT) || 'Could have'] || '#888'}
                />
            )}

            {/*
             * The status badge is temporarily disabled for solution/story/experiment nodes
             * (Status dropdown for those types is likewise hidden — see NodePanel.tsx's
             * SHOW_NODE_STATUS_DROPDOWN) while keeping StatusCircle itself intact for later
             * refactor/repurposing. Opportunity's badge (driven by "Validated?", not the
             * disabled Status field) is unaffected.
             */}
            {node.type === 'opportunity' && (
                <StatusCircle node={node} cx={NODE_W - 12} cy={12} />
            )}

            {renderActionControls()}
        </g>
    );
}
